import React, { forwardRef, useRef, useState } from 'react';

import type { InputBaseProps } from '@mui/material';
import { ButtonBase, Divider, InputAdornment, InputBase, Menu } from '@mui/material';
import useForkRef from '@mui/utils/useForkRef';
import clsx from 'clsx';
import { not } from 'ramda';
import { ArrowDownIcon } from 'src/components/icons/ArrowDownIcon';
import { ClientTransforms } from 'src/editor/transforms';
import { useEditor } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<StyleProps>()(
  (theme, { divStyles, iconStyles, open, showDivider }) => ({
    button: {
      color: open ? theme.palette.primary.main : undefined,
      height: divStyles.height,
      width: iconStyles.right * 2 + iconStyles.width,
    },
    div: {
      '&:hover': {
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
        // Reset on mouse devices
        backgroundColor: theme.palette.action.hover,
        textDecoration: 'none',
      },
      border: open
        ? `1px solid ${theme.palette.primary.main}`
        : `1px solid ${theme.appColors.rule}`,
      borderRadius: theme.spacing(0.375),
      boxSizing: 'border-box',
      display: 'flex',
      margin: theme.spacing(0, 0.75, 0, 0.75),
      ...divStyles,
    },
    divider: {
      height: divStyles.height * 0.75,
      visibility: showDivider ? ('visible' as const) : ('hidden' as const),
      width: 1,
    },
    inputRoot: {
      fontSize: 14,
      padding: theme.spacing(0, 0, 0, 1),
    },
    positionEnd: {
      marginLeft: theme.spacing(0.5),
    },
  })
);

interface SelectTextProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'children' | 'onChange'
> {
  buttonAttribute: string;
  children: React.ReactNode;
  divStyles: {
    height: number;
    width: number;
  };
  editorAction: (value: string) => void;
  iconStyles: {
    height: number;
    right: number;
    width: number;
  };
  inputAttribute: string;
  inputDOMElement: HTMLInputElement | null;
  inputRef: React.Ref<HTMLInputElement>;
  onChange: InputBaseProps['onChange'];
  onClose: () => void;
  onOpen: () => void;
  open: boolean;
  value: string;
  disabled?: boolean;
  onFocusInput?: () => void;
  onMouseOutInputWrapper?: () => void;
  onMouseOverInputWrapper?: () => void;
}

interface StyleProps {
  divStyles: SelectTextProps['divStyles'];
  iconStyles: SelectTextProps['iconStyles'];
  open: SelectTextProps['open'];
  showDivider: boolean;
}

const SelectTextWithRef = forwardRef(function SelectText(
  props: SelectTextProps,
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  const {
    buttonAttribute,
    children,
    className,
    disabled,
    divStyles,
    editorAction,
    iconStyles,
    inputAttribute,
    inputDOMElement,
    inputRef,
    onBlur,
    onChange,
    onClose,
    onFocus,
    onFocusInput,
    onMouseLeave,
    onMouseOutInputWrapper,
    onMouseOver,
    onMouseOverInputWrapper,
    onOpen,
    open,
    value,
    ...rootProps
  } = props;

  const editor = useEditor();
  const divRef = useRef<HTMLDivElement | null>(null);
  const [showDivider, setShowDivider] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const { classes } = useStyles({ divStyles, iconStyles, open, showDivider });
  const handleRootRef = useForkRef(divRef, ref);

  function handleClick(): void {
    if (not(open)) {
      setAnchorEl(divRef.current);
      onOpen();
    }
  }

  function handleClose(): void {
    setAnchorEl(null);
    onClose();
    ClientTransforms.focus(editor);
  }

  function handleFocusDiv(event: React.FocusEvent<HTMLDivElement>): void {
    setShowDivider(true);
    onFocus?.(event);
  }

  function handleBlurDiv(event: React.FocusEvent<HTMLDivElement>): void {
    setShowDivider(false);
    onBlur?.(event);
  }

  function handleMouseOver(event: React.MouseEvent<HTMLDivElement>): void {
    setShowDivider(true);
    if (onMouseOverInputWrapper) onMouseOverInputWrapper();
    onMouseOver?.(event);
  }

  function handleMouseOut(event: React.MouseEvent<HTMLDivElement>): void {
    if (inputDOMElement) {
      if (document.activeElement !== inputDOMElement) setShowDivider(false);
    }

    if (onMouseOutInputWrapper) onMouseOutInputWrapper();
    onMouseLeave?.(event);
  }

  function handleFocusInput(): void {
    if (inputDOMElement) {
      setShowDivider(true);
      inputDOMElement.select();

      if (onFocusInput) onFocusInput();
    }
  }

  function handleKeyDown(event: React.KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      editorAction(value);
    }
  }

  return (
    <>
      <div
        {...rootProps}
        ref={handleRootRef}
        className={clsx(classes.div, className)}
        onBlur={handleBlurDiv}
        onFocus={handleFocusDiv}
        onMouseLeave={handleMouseOut}
        onMouseOver={handleMouseOver}
      >
        <InputBase
          classes={{ input: classes.inputRoot }}
          disabled={disabled}
          endAdornment={
            <InputAdornment classes={{ positionEnd: classes.positionEnd }} position="end">
              <Divider className={classes.divider} orientation="vertical" />
              <ButtonBase
                className={classes.button}
                data-cy={buttonAttribute}
                disabled={disabled}
                onClick={handleClick}
              >
                <ArrowDownIcon iconStyles={iconStyles} />
              </ButtonBase>
            </InputAdornment>
          }
          inputProps={{ 'data-cy': inputAttribute }}
          inputRef={inputRef}
          value={value}
          onChange={onChange}
          onFocus={handleFocusInput}
          onKeyDown={handleKeyDown}
        />
      </div>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={open}
        variant="menu"
        onClose={handleClose}
      >
        {children}
      </Menu>
    </>
  );
});

export const SelectText = React.memo(SelectTextWithRef);
